/**
 * Interest in employment with BondIT Group (backend#882), and employee status
 * (backend#917).
 *
 * The interest is an optional boolean: `true` interested, `false` not
 * interested, and `null`/absent "not stated" (every consultant who joined
 * before the question existed). Admins see it for every consultant; a
 * consultant only ever sees their own.
 *
 * An employee is a consultant whose profile company is one of BondIT's own
 * companies. The backend derives it (`is_employee` / `employer_name`); for an
 * employee the interest question is moot, so the views show "Employed by
 * <company>" instead of it.
 */
export type EmploymentInterest = boolean | null | undefined;

export const EMPLOYMENT_INTEREST_QUESTION = "I'm interested in employment with BondIT Group";

/** Label of the interest answer wherever it is shown or edited. */
export const EMPLOYMENT_INTEREST_LABEL = "Interested in employment";

/** Label of the row that replaces the interest answer for an employee. */
export const EMPLOYMENT_STATUS_LABEL = "Employment";

export const employmentInterestLabel = (value: EmploymentInterest): string => {
  if (value === true) {
    return "Interested";
  }
  if (value === false) {
    return "Not interested";
  }
  return "Not stated";
};

/** The employee status fields the backend returns (backend#917). */
export interface EmployeeStatus {
  is_employee?: boolean | null;
  employer_name?: string | null;
}

/** True only for an employee; absent fields (older backends) read as no. */
export const isEmployee = (status: EmployeeStatus | null | undefined): boolean =>
  status?.is_employee === true;

/** "Employed by BondIT Denmark ApS"; the backend always names the employer, but a
 *  missing name still reads sensibly. */
export const employedByLabel = (employerName: string | null | undefined): string =>
  employerName ? `Employed by ${employerName}` : "Employed by BondIT";

/**
 * The admin list filter. "" is "all"; "employees" lists the employees;
 * "true" / "false" split everyone else on their answer, where "false" is
 * everyone who has not said yes (said no, or never answered). Employees match
 * neither answer — the backend's semantics.
 */
export type EmploymentFilter = "" | "employees" | "true" | "false";

export const EMPLOYMENT_FILTER_OPTIONS: {
  value: EmploymentFilter;
  label: string;
}[] = [
  { value: "", label: "All employment" },
  { value: "employees", label: "Employees" },
  { value: "true", label: "Interested in employment" },
  { value: "false", label: "Not interested / not stated" },
];

/** Query params for the list endpoint; undefined values send no param. */
export const toEmploymentFilterParams = (
  filter: EmploymentFilter
): { interested_in_employment?: boolean; is_employee?: boolean } => {
  if (filter === "employees") {
    return { is_employee: true };
  }
  if (filter === "") {
    return {};
  }
  return { interested_in_employment: filter === "true" };
};
